import { Link } from "react-router-dom";
import { PANEL_MENU } from "@/mocks/panelContent";
import { useAdmin } from "@/store/AdminContext";
import type { PanelSectionId } from "@/pages/painel/types";

interface PanelSidebarProps {
  active: PanelSectionId;
  onSelect: (id: PanelSectionId) => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

function NavList({ active, onSelect }: { active: PanelSectionId; onSelect: (id: PanelSectionId) => void }) {
  return (
    <nav aria-label="Menu do painel" className="flex flex-col gap-1">
      {PANEL_MENU.map((item) => {
        const isActive = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id as PanelSectionId)}
            aria-current={isActive ? "page" : undefined}
            className={`flex cursor-pointer items-center gap-3 whitespace-nowrap rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
              isActive
                ? "bg-primary-500 text-background-50"
                : "text-foreground-700 hover:bg-background-100"
            }`}
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className={`${item.icon} text-base`} />
            </span>
            {item.label}
          </button>
        );
      })}
    </nav>
  );
}

function AdminAccessBlock() {
  const { notConnectedMessage } = useAdmin();
  return (
    <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-3">
      <div className="flex items-center gap-2 text-xs font-medium text-foreground-700">
        <i className="ri-eye-line text-sm text-secondary-700" />
        Painel demonstrativo
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
        {notConnectedMessage}
      </p>
    </div>
  );
}

export default function PanelSidebar({ active, onSelect, mobileOpen, onCloseMobile }: PanelSidebarProps) {
  const handleSelect = (id: PanelSectionId) => {
    onSelect(id);
    onCloseMobile();
  };

  return (
    <>
      {/* Desktop */}
      <aside className="hidden w-[280px] shrink-0 flex-col border-r border-background-200 bg-background-50 lg:flex">
        <div className="border-b border-background-200 px-5 py-5">
          <Link to="/" className="flex cursor-pointer items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-primary-300 bg-primary-50">
              <i className="ri-leaf-line text-base text-primary-600" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-semibold text-primary-700">Amora Belle</span>
              <span className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-foreground-500">painel</span>
            </span>
          </Link>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-5">
          <NavList active={active} onSelect={handleSelect} />

          <Link
            to="/"
            className="mt-4 flex cursor-pointer items-center gap-3 whitespace-nowrap rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-external-link-line text-base" />
            </span>
            Visualizar site
          </Link>
        </div>

        <div className="border-t border-background-200 p-4">
          <AdminAccessBlock />
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-[130] lg:hidden" role="dialog" aria-modal="true" aria-label="Menu do painel">
          <button
            type="button"
            aria-label="Fechar menu do painel"
            onClick={onCloseMobile}
            className="absolute inset-0 h-full w-full cursor-pointer bg-foreground-950/45"
          />
          <div className="ab-panel-in absolute left-0 top-0 flex h-[100dvh] w-[84%] max-w-xs flex-col bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-4">
              <span className="font-heading text-xl text-primary-700">Painel Amora Belle</span>
              <button
                type="button"
                onClick={onCloseMobile}
                aria-label="Fechar menu do painel"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300 text-foreground-700"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <NavList active={active} onSelect={handleSelect} />
              <Link
                to="/"
                onClick={onCloseMobile}
                className="mt-4 flex cursor-pointer items-center gap-3 whitespace-nowrap rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-external-link-line text-base" />
                </span>
                Visualizar site
              </Link>
              <div className="mt-4">
                <AdminAccessBlock />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
